import {CameraMode, IPcbBoardLayerCopper, MultiRoutingGroup, PcbNodeTypes} from "@buildwithflux/core";
import {IVector2, IVector3} from "@buildwithflux/models";
import {Group, Vector2, Vector3} from "three";

import {R3FStateEvents} from "modules/r3f_events";

export enum LayerViewState {
    opened = "open",
    closed = "close",
}

export type ControlsState = {
    offset: IVector3;
    position: IVector3;
    target: IVector3;
    zoom: number;
};
type InteractionType =
    | "DEFAULT"
    | "DRAGGING"
    | "PANNING"
    | "MEASURING"
    | "ROUTING"
    | "POLYGON_DRAW"
    // It's possible to pan while routing using middle click or space-drag.
    // So you end up in a state in which you pause routing to pan, and routing mode has to be restored after pan is finished.
    | "PANNING_PAUSEDROUTING"
    | "PANNING_PAUSEDMEASURING"
    | "PANNING_PAUSEDPOLYGON_DRAW"
    | "DISABLED";

export type Interaction =
    | "START_DRAGGING"
    | "START_PANNING"
    | "START_MEASURING"
    | "END_INTERACTION"
    | "DISABLE"
    | "ABORT_INTERACTION";

export type PlaceholderSegments = {
    [uid: string]: {
        layer: IPcbBoardLayerCopper;
        traceWidth: number;
        uid: string;
        start: Vector3;
        end: Vector3;
    };
};

export type PcbEditorUiTransientState = {
    interactionAborted: boolean;
    interactionState: {state: InteractionType};
    disableSelectionCommit: boolean;
    routingState: RoutingState | undefined;
    dragState: DragState | undefined;
    multiRoutingState: MultiRoutingState;
    multiRoutingCache: MultiRoutingCache | undefined;
    multiRoutingHover: boolean;
    disableMultiRouting: boolean;
    selectedPolygonToEditUid: string | undefined;
    polygonDrawState: PolygonDrawState | PolygonEditState | undefined;
    threeDSceneContentGroupMesh: Group | null;
    projectedHumanizedTraceWidth: string | undefined;
    selectedHumanizedTraceWidth: string | undefined;
    activeTouchpointContext: ActiveTouchpointContext | null;
    measuringStartPoint: Vector2;
    pcbEditorR3FStore: R3FStateEvents | null;
    previewSegments: PreviewSegment[];
    placeholderSegments: PlaceholderSegments | null;
    /** Cache to store selected via configurations for specified connected layers and via options for smart vias */
    smartViaConfigsCache: Record<string, string[]>;
    /** Something analogous to "pcb fully loaded" -- as close as we can get to the time when the
     * user can reasonably start interacting after load. This attempts to account for the initial
     * bake etc (unlike the "document is loaded" state). Use this to display loading spinners
     * (instead of partial states) before things are fully ready.
     */
    initialPcbStateReady: boolean;
    enableStatsViewer: boolean;
};
export type PcbEditorUiPersistentState = {
    cameraMode: CameraMode;
    flippedCamera: boolean;
    layerViewState: LayerViewState;
    pcbEnabledSnapping: boolean;
    pcbEnabledMeasurements: boolean;
    pcbThreeDModelsVisibility: boolean;
    pcbSnappingBeforeTemporaryDisable: boolean | null;
};
/**
 * Represents a single entry in the routing history stack.
 * Used to restore the routing state when undo is pressed during routing.
 */
export type RoutingHistoryEntry = {
    position: IVector2;
    /** @deprecated This field is not reliable anymore, do not use to determine what is connected */
    lastVertexId: string;
    lastNodeId: string;
    layer: IPcbBoardLayerCopper;
};

export type RoutingState = {
    netId: string;
    position: IVector2;
    /** @deprecated This field is not reliable anymore, do not use to determine what is connected */
    lastVertexId: string;
    lastNodeId: string;
    layer: IPcbBoardLayerCopper;
    flip: boolean;
    freeRoutingMode: boolean;
    snapToSegment: SnapToSegment | null;
    routeFromSegment: SnapToSegment | null;
    snappedVertexUid: string | null;
    snappedPos: IVector3 | null;
    layerOffset: number | undefined;

    overrideTraceWidth: number | undefined;

    /**
     * Stack of previous routing positions for undo support.
     * When a new segment is added, the previous position is pushed to this stack.
     * When undo is pressed during routing, we pop from this stack to restore the previous position.
     */
    history: RoutingHistoryEntry[];

    /**
     * Stack of future routing positions for redo support.
     * When undo is pressed, the current position is pushed to this stack.
     * When redo is pressed during routing, we pop from this stack to restore the position.
     * This stack is cleared when a new segment is added (since redo is only valid after undo).
     */
    future: RoutingHistoryEntry[];
};
export type SnapToSegment = {
    start: IVector2;
    end: IVector2;
    /** @deprecated This field is not reliable anymore, do not use to determine what is connected */
    startId: string;
    /** @deprecated This field is not reliable anymore, do not use to determine what is connected */
    endId: string;
    id: string;
};
export type ActiveTouchpointContext = {
    uid: string;
    parentUid: string;
    nodeType: PcbNodeTypes;
    position: IVector2;
    netId?: string;
    routeFromSegment?: SnapToSegment;
    edge?: "start" | "end" | "midpoint";
};

export type MultiRoutingCache = {
    /** For each net, is that net connected or does it have airwires? */
    netConnected?: Record<string, boolean>;
    /** For each group, all the absolute vertex positions (traces+pads+vias) that are routable to with multi-routing */
    vertexInfo: Record<string, MultiRoutingCacheItem[]>;
    multiRoutingGroups: MultiRoutingGroup[];
    multiRoutingGroupsPerNet: Record<string, MultiRoutingGroup[]>;
};
export type MultiRoutingCacheItem = {
    nodeUid: string;
    position: Vector2;
    netUid: string;
    type: PcbNodeTypes;
    notRoutable?: boolean;
    edge?: "start" | "end";
    connectedSegmentUid?: string;
};

export type MultiRoutingState =
    | undefined
    | {
          state: "MULTIROUTING" | "MULTIROUTING_PAUSED";
          groupUid: string;
          targetDistance: number;
      };

export type DragState =
    | undefined
    | {
          state: "dragPolygonPoint" | "dragTrace" | "dragVia" | "dragToMove" | null;
      };

export type PolygonDrawState = {
    mode: "draw";
    points: IVector2[];
    netId: string;
    layer: "All" | IPcbBoardLayerCopper;
    startTouchpointId?: string;
};
export type PolygonEditState = {
    mode: "edit";
    polygonNodeUid: string;
    draggedPoint: {
        i: number;
        type: "real" | "virtual";
    };
    /** Mutable, updated every frame */
    projectedAbsolutePos: Vector2;
};

export type PreviewSegment = {
    layoutUid: string;
    layer: string;
    traceWidth: number;
    startPos: Vector3;
    endPos: Vector3;
};

export type PcbEditorUiState = PcbEditorUiTransientState & PcbEditorUiPersistentState;

export type PcbEditorUiActions = {
    // Placeholder segments are segments that are temporarily rendered while the insert/baking logic 'roundtrip'
    // is underway - after user clicks to commit traces during routing. Since the roundtrip is asynchronous (and
    // usually slow) we need placeholders to avoid the traces disappearing during the roundtrip.
    appendPlaceholderSegments: (placeholderSegments: PlaceholderSegments) => void;
    removePlaceholderSegments: (uid: string[]) => void;

    interact: (interaction: Interaction) => void;
    setDisableSelectionCommit: (disableSelectionCommit: boolean) => void;
    resetAbort: () => void;
    setActiveTouchpointContext: (activeTouchpointContext: ActiveTouchpointContext | null) => void;
    setCameraMode: (mode: CameraMode) => void;
    setDisableMultiRouting: (disableMultiRouting: boolean) => void;
    setFlippedCamera: (flip: boolean) => void;

    // TODO: consider eventually removing these, and state.
    setHumanizedProjectedTraceWidth: (humanizedWidth: string) => void;
    setHumanizedSelectedTraceWidth: (humanizedWidth: string | undefined) => void;

    setLayerViewState: (state: LayerViewState) => void;
    setMultiRoutingCache: (multiRoutingCache: MultiRoutingCache) => void;
    setMultiRoutingHover: (multiRoutingHover: boolean) => void;
    setMultiRoutingState: (multiRoutingState: MultiRoutingState) => void;
    setDragState: (dragState: DragState) => void;
    setPcbEditorR3FStore: (pcbEditorR3FStore: R3FStateEvents | null) => void;
    setPcbEnabledSnapping: (pcbEnabledSnapping: boolean) => void;
    setPcbSnappingBeforeTemporaryDisable: (pcbSnappingBeforeTemporaryDisable: boolean | null) => void;
    setPcbEnabledMeasurements: (pcbEnabledMeasurements: boolean) => void;
    setPcbThreeDModelsVisibility: (pcbThreeDModelsVisibility: boolean) => void;
    setPreviewSegments: (previewSegments: PreviewSegment[]) => void;
    setRoutingSnap: (snappedVertexUid: string | null, snappedPos: IVector3 | null) => void;
    setRoutingState: (
        position: IVector2,
        lastVertexId?: string,
        lastNodeId?: string,
        netId?: string,
        layer?: IPcbBoardLayerCopper,
        flip?: boolean,
        freeRoutingMode?: boolean,
        snapToSegment?: SnapToSegment | null,
        routeFromSegment?: SnapToSegment | null,
    ) => void;
    setRoutingStateOverrideTraceWidth: (overrideTraceWidth: number | undefined) => void;
    setRoutingStateLayeringOffset: (layerOffset: number) => void;
    /**
     * Pops the last entry from the routing history stack and restores the routing state to that position.
     * Used when undo is pressed during routing to go back to the previous routing position.
     * @returns true if there was a history entry to pop, false if the history was empty (routing should stop)
     */
    popRoutingHistory: () => boolean;
    /**
     * Pops the last entry from the routing future stack and restores the routing state to that position.
     * Used when redo is pressed during routing to go forward to the next routing position.
     * @returns true if there was a future entry to pop, false if the future was empty
     */
    pushRoutingFuture: () => boolean;
    setThreeDSceneContentGroupMesh: (group: Group | null) => void;
    toggleMultiRouting: () => void;
    // Cache to store selected via configurations for specified connected layers and via options for smart vias
    // Computed in the workers after each bake (if needed) and stored in the store to be used in runtime
    // Use the `SmartViaConfigsCache` type to interact with this cache
    setSmartViaConfigsCache: (smartViaConfigsCache: Record<string, string[]>) => void;
    setSelectedPolygonToEditUid: (selectedPolygonToEditUid: string | undefined) => void;
    startDrawingPolygon: (
        position: Vector2,
        netId: string,
        layer?: IPcbBoardLayerCopper,
        startTouchpointId?: string,
    ) => void;
    polygonDrawAddPoint: (position: IVector2) => void;
    polygonDrawRemoveLastPoint: () => void;
    stopDrawingPolygon: () => void;
    startEditingPolygon: (
        polygonNodeUid: string,
        draggedPoint: PolygonEditState["draggedPoint"],
        startingAbsolutePos: Vector2,
    ) => void;
    stopEditingPolygon: () => void;
    setInitialPcbStateReady: (isReady: boolean) => void;
    setEnableStatsViewer: (enableStatsViewer: boolean) => void;
};
